import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";

// Sends each exam student their own result AND records it as a campaign,
// auto-named "<exam_name> - <exam_paper_id>" (no separate campaign step).
const CREATE_EXAM_RESULT_CAMPAIGN = gql`
  mutation($input: CreateExamResultCampaignInput!) {
    createExamResultCampaign(input: $input) {
      status
      total_recipients
      sent_count
      failed_count
      absent_count
      scheduled_count
      send_at_offset_sec
    }
  }
`;

// Pull back SMS still inside the 5-minute cancel window.
const CANCEL_SCHEDULED_SMS = gql`
  mutation($input: CancelScheduledSmsInput!) {
    cancelScheduledSms(input: $input) { cancelled }
  }
`;

// Cancel one scheduled batch (a single "send") by its analysis-row id.
const CANCEL_SCHEDULED_SMS_BATCH = gql`
  mutation($id: Int!) {
    cancelScheduledSmsBatch(id: $id) { cancelled }
  }
`;

// Re-send only to parents whose WhatsApp delivery failed on prior campaigns.
const RESEND_FAILED_EXAM_CAMPAIGN = gql`
  mutation($input: ResendFailedExamCampaignInput!) {
    resendFailedExamCampaign(input: $input) {
      status
      total_recipients
    }
  }
`;

const GET_EXAM_EVENT_TEMPLATES = gql`
  query($employee_id: Int, $channel: String) {
    getExamEventTemplates(employee_id: $employee_id, channel: $channel) {
      event_code
      template_name
      language
      body
    }
  }
`;

// Past campaigns for this exam + their WhatsApp delivery analysis.
const GET_EXAM_RESULT_CAMPAIGNS = gql`
  query($institute_id: Int!, $exam_paper_id: Int) {
    getExamResultCampaigns(institute_id: $institute_id, exam_paper_id: $exam_paper_id) {
      id
      campaign_name
      workflow_status_id
      attempted
      sent
      delivered
      read
      failed
      scheduled
      cancelled
      channel
    }
  }
`;

// Drill-down: the parents behind a campaign row's counts.
const GET_CAMPAIGN_RECIPIENTS = gql`
  query($id: Int!, $channel: String!) {
    getCampaignRecipients(id: $id, channel: $channel) {
      mobile
      name
      status
      sent_at
      error
    }
  }
`;

const CHANNELS = [
  { key: "sms", label: "SMS", icon: "fa-sms", desc: "DLT result template per student" },
  { key: "whatsapp", label: "WhatsApp", icon: "fa-whatsapp", desc: "Approved result template per student" },
  // { key: "push", label: "Mobile Push", icon: "fa-bell", desc: "App notification with each student's result" },
];

// Real WhatsApp brand mark (official green speech-bubble + handset) as inline SVG,
// so the channel always shows the recognizable logo instead of a tinted glyph.
const WhatsAppIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="#25D366"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  </svg>
);

// SMS brand mark — blue chat bubble with "SMS" ellipsis dots, so the channel
// shows a recognizable icon instead of a tinted glyph.
const SmsIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
    <path
      fill="#2563eb"
      d="M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2z"
    />
    <circle cx="8" cy="9" r="1.4" fill="#fff" />
    <circle cx="12" cy="9" r="1.4" fill="#fff" />
    <circle cx="16" cy="9" r="1.4" fill="#fff" />
  </svg>
);

class NotifyExamStudentsModal extends Component {
  constructor(props) {
    super(props);
    this.state = { channel: "sms", recipients: props.recipients || "all", sending: false, result: null, error: "", templates: null, templatesChannel: null, templatesLoading: false, showTemplate: false, campaigns: [], campaignsLoading: false, activeTab: "recipients", resending: false, resendNote: "",
      // Drill-down: which (campaignId, statusKey) is expanded, a per-campaign recipient cache, and a loading flag.
      drill: null, recipientsCache: {}, recipientsLoading: false };
  }

  // Toggle the recipient list for a campaign row filtered to the clicked count (statusKey).
  toggleRecipients = async (campaign, statusKey) => {
    const same = this.state.drill && this.state.drill.id === campaign.id && this.state.drill.statusKey === statusKey;
    if (same) { this.setState({ drill: null }); return; }
    this.setState({ drill: { id: campaign.id, statusKey } });
    if (this.state.recipientsCache[campaign.id]) return; // already fetched
    this.setState({ recipientsLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_CAMPAIGN_RECIPIENTS,
        variables: { id: parseInt(campaign.id), channel: campaign.channel || this.state.channel },
        fetchPolicy: "network-only",
      });
      const list = (res.data && res.data.getCampaignRecipients) || [];
      this.setState((s) => ({ recipientsLoading: false, recipientsCache: { ...s.recipientsCache, [campaign.id]: list } }));
    } catch (e) {
      this.setState((s) => ({ recipientsLoading: false, recipientsCache: { ...s.recipientsCache, [campaign.id]: [] } }));
    }
  };

  // Which recipient statuses each clickable column reveals (matches the count definitions).
  statusesFor = (statusKey) => ({
    scheduled: ["scheduled"],
    sent: ["sent", "delivered", "read"],
    delivered: ["delivered", "read"],
    read: ["read"],
    failed: ["failed"],
    cancelled: ["cancelled"],
  }[statusKey] || []);

  componentDidUpdate(prevProps, prevState) {
    // Load templates when the preview is opened, or when channel changes while open.
    const justOpenedPreview = this.state.showTemplate && !prevState.showTemplate;
    const channelChanged = this.state.channel !== prevState.channel;
    if (this.props.show && this.state.showTemplate && (justOpenedPreview || channelChanged || this.state.templatesChannel !== this.state.channel) && !this.state.templatesLoading) {
      this.fetchTemplates();
    }
    // Load campaign history/analysis when the modal opens.
    if (this.props.show && !prevProps.show) {
      this.fetchCampaigns();
    }
  }

  fetchCampaigns = async () => {
    this.setState({ campaignsLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_RESULT_CAMPAIGNS,
        variables: { institute_id: parseInt(Cookies.get("institutionid")) || 0, exam_paper_id: parseInt(this.props.examId) || 0 },
        fetchPolicy: "network-only",
      });
      this.setState({ campaigns: (res.data && res.data.getExamResultCampaigns) || [], campaignsLoading: false });
    } catch (e) {
      this.setState({ campaigns: [], campaignsLoading: false });
    }
  };

  fetchTemplates = async () => {
    const channel = this.state.channel;
    this.setState({ templatesLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_EVENT_TEMPLATES,
        variables: { employee_id: parseInt(Cookies.get("user_id") || Cookies.get("id")) || 0, channel },
        fetchPolicy: "network-only",
      });
      const list = (res.data && res.data.getExamEventTemplates) || [];
      const map = {};
      list.forEach((t) => { map[t.event_code] = t; });
      this.setState({ templates: map, templatesChannel: channel, templatesLoading: false });
    } catch (e) {
      this.setState({ templates: {}, templatesChannel: channel, templatesLoading: false });
    }
  };

  reset = () => this.setState({ channel: "sms", recipients: this.props.recipients || "all", sending: false, result: null, error: "", templates: null, templatesChannel: null, showTemplate: false, activeTab: "recipients", drill: null, recipientsCache: {} });

  // Students matching the selected scope (drives the preview list + count).
  getRecipients = () => {
    const list = this.props.students || [];
    if (this.state.recipients === "attended") return list.filter((s) => s.attempted_exam == true);
    if (this.state.recipients === "absent") return list.filter((s) => s.attempted_exam != true);
    return list;
  };

  handleClose = () => {
    this.reset();
    this.props.onHide();
  };

  handleSend = async () => {
    this.setState({ sending: true, error: "", result: null });
    try {
      // Campaign name is the exam_paper_id.
      const campaignName = `${this.props.examId || ""}`.trim();
      const res = await this.props.client.mutate({
        mutation: CREATE_EXAM_RESULT_CAMPAIGN,
        variables: {
          input: {
            institute_id: parseInt(Cookies.get("institutionid")) || 0,
            campaign_name: campaignName,
            exam_paper_id: parseInt(this.props.examId) || 0,
            channel: this.state.channel,
            recipients_scope: this.state.recipients,
            scheduled_at: "immediate",
            inserted_by: parseInt(Cookies.get("user_id") || Cookies.get("id")) || 0,
          },
        },
      });
      const c = (res.data && res.data.createExamResultCampaign) || {};
      this.setState({
        result: {
          total: c.total_recipients || 0,
          sent: c.sent_count || 0,
          failed: c.failed_count || 0,
          absent: c.absent_count,
          channel: this.state.channel,
          // SMS is queued for a delayed send — surface the count + window so the parent
          // can be given a Cancel option before the worker dispatches.
          scheduled: c.scheduled_count || 0,
          sendAtOffsetSec: c.send_at_offset_sec || 0,
          errors: c.errors || [],
        },
        cancelled: 0,
        sending: false,
        activeTab: "analysis",
      }, this.fetchCampaigns);
    } catch (err) {
      this.setState({
        error: (err.graphQLErrors && err.graphQLErrors.map((x) => x.message).join(", ")) || "Failed to send notifications.",
        sending: false,
      });
    }
  };

  // Cancel scheduled SMS still in the 5-minute window (campaign_name = exam_paper_id).
  handleCancelScheduled = async () => {
    this.setState({ cancelling: true });
    try {
      const res = await this.props.client.mutate({
        mutation: CANCEL_SCHEDULED_SMS,
        variables: { input: {
          institute_id: parseInt(Cookies.get("institutionid")) || 0,
          campaign_name: `${this.props.examId || ""}`.trim(),
        } },
      });
      const cancelled = (res.data && res.data.cancelScheduledSms && res.data.cancelScheduledSms.cancelled) || 0;
      this.setState((s) => ({ cancelling: false, cancelled, result: { ...s.result, scheduled: Math.max(0, (s.result?.scheduled || 0) - cancelled) } }), this.fetchCampaigns);
    } catch (e) {
      this.setState({ cancelling: false, error: "Couldn't cancel — messages may have already been sent." });
    }
  };

  // Cancel ONE scheduled batch (a single send) by its analysis-row id.
  handleCancelBatch = async (campaign) => {
    this.setState({ cancellingId: campaign.id });
    try {
      const res = await this.props.client.mutate({
        mutation: CANCEL_SCHEDULED_SMS_BATCH,
        variables: { id: parseInt(campaign.id) },
      });
      const cancelled = (res.data && res.data.cancelScheduledSmsBatch && res.data.cancelScheduledSmsBatch.cancelled) || 0;
      this.setState({ cancellingId: null }, this.fetchCampaigns);
      if (!cancelled) this.setState({ error: "Nothing to cancel — this batch may have already been sent." });
    } catch (e) {
      this.setState({ cancellingId: null, error: "Couldn't cancel — messages may have already been sent." });
    }
  };

  handleResendFailed = async () => {
    this.setState({ resending: true, resendNote: "", error: "" });
    try {
      const res = await this.props.client.mutate({
        mutation: RESEND_FAILED_EXAM_CAMPAIGN,
        variables: {
          input: {
            institute_id: parseInt(Cookies.get("institutionid")) || 0,
            exam_paper_id: parseInt(this.props.examId) || 0,
            channel: this.state.channel,
            inserted_by: parseInt(Cookies.get("user_id") || Cookies.get("id")) || 0,
          },
        },
      });
      const c = (res.data && res.data.resendFailedExamCampaign) || {};
      const note = c.status === "no_failures"
        ? "No failed messages to resend."
        : `Re-queued ${c.total_recipients || 0} failed recipient(s).`;
      this.setState({ resending: false, resendNote: note }, this.fetchCampaigns);
    } catch (err) {
      this.setState({
        resending: false,
        resendNote: "",
        error: (err.graphQLErrors && err.graphQLErrors.map((x) => x.message).join(", ")) || "Failed to resend.",
      });
    }
  };

  render() {
    const { show } = this.props;
    const { channel, recipients, sending, result, error } = this.state;
    // LIVE SMS totals for this exam (from campaigns) so the result banner reflects cancellations
    // made either way (per-batch or cancel-all), not the stale send-time count.
    const smsCards = (this.state.campaigns || []).filter((c) => (c.channel || "whatsapp") === "sms");
    const liveScheduled = smsCards.reduce((n, c) => n + (c.scheduled || 0), 0);
    const liveCancelled = smsCards.reduce((n, c) => n + (c.cancelled || 0), 0);
    const liveSent = smsCards.reduce((n, c) => n + (c.sent || 0), 0);
    // Until campaigns are fetched after a send, fall back to the send-time count so the banner
    // doesn't briefly flip to "closed" before the live data arrives.
    const pendingScheduled = smsCards.length ? liveScheduled : (result ? (result.scheduled || 0) : 0);
    const heading = recipients === "absent" ? "Notify Absentees"
      : recipients === "attended" ? "Notify Present Students"
        : "Notify Students — Result";
    const recipientList = this.getRecipients();
    // ONE result template per exam, chosen by the paper's target exam (neetjee.exam):
    //   1 = NEET -> bipc_result (Botany | Zoology | Physics | Chemistry)
    //   2 = JEE  -> mpc_result  (Physics | Chemistry | Maths)
    // Present and absent share it — absentees get "ab/<max>" in place of each mark — so
    // there is no separate exam_absent template regardless of the selected scope.
    const eventCodesForScope = [Number(this.props.examType) === 1 ? "bipc_result" : "mpc_result"];
    const templates = this.state.templates || {};

    return (
      <Modal show={show} onHide={this.handleClose} centered size="md">
        <Modal.Header closeButton style={{ background: "#7c3aed", color: "#fff", border: "none" }}>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 600 }}>
            <i className="fas fa-bell mr-2" /> {heading}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {result ? (
            (result.scheduled > 0 && pendingScheduled > 0) ? (
              // SMS is queued for a delayed send AND some are still pending — show the cancel window.
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontWeight: 700, color: "#b45309", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fas fa-clock" />
                  Scheduled — sending in {Math.round((result.sendAtOffsetSec || 300) / 60)} minute(s)
                </div>
                <div style={{ fontSize: 13, color: "#334155" }}>
                  <strong style={{ color: "#b45309" }}>{pendingScheduled}</strong> SMS queued to parents.
                  {this.state.cancelled > 0 ? <> &nbsp;·&nbsp; <strong style={{ color: "#dc2626" }}>{this.state.cancelled}</strong> cancelled.</> : null}
                </div>
                <div style={{ fontSize: 11.5, color: "#92400e", marginTop: 6 }}>
                  You can pull these back before they send. After the window they dispatch automatically and appear under Campaign Analysis.
                </div>
                <button type="button" onClick={this.handleCancelScheduled} disabled={this.state.cancelling}
                  style={{ marginTop: 12, border: "1px solid #dc2626", background: "#fff", color: "#dc2626", borderRadius: 8, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: this.state.cancelling ? "default" : "pointer", opacity: this.state.cancelling ? 0.6 : 1 }}>
                  <i className="fas fa-ban mr-1" /> {this.state.cancelling ? "Cancelling…" : "Cancel scheduled SMS"}
                </button>
              </div>
            ) : (result.scheduled > 0) ? (
              // Was a scheduled SMS send, but nothing is pending anymore — cancelled and/or dispatched.
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontWeight: 700, color: (liveCancelled > 0 && liveSent === 0) ? "#dc2626" : "#334155", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className={(liveCancelled > 0 && liveSent === 0) ? "fas fa-ban" : "fas fa-check-circle"} />
                  {(liveCancelled > 0 && liveSent === 0) ? "Scheduled SMS cancelled" : "Scheduled window closed"}
                </div>
                <div style={{ fontSize: 13, color: "#334155" }}>
                  {liveSent > 0 ? <><strong style={{ color: "#16a34a" }}>{liveSent}</strong> sent</> : null}
                  {liveSent > 0 && liveCancelled > 0 ? <> &nbsp;·&nbsp; </> : null}
                  {liveCancelled > 0 ? <><strong style={{ color: "#dc2626" }}>{liveCancelled}</strong> cancelled</> : null}
                  {liveSent === 0 && liveCancelled === 0 ? "No SMS pending." : "."}
                </div>
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
                  See the <strong>Campaign Analysis</strong> tab for the full breakdown.
                </div>
              </div>
            ) : (
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontWeight: 700, color: "#7c3aed", marginBottom: 6 }}>
                <i className="fas fa-paper-plane mr-2" />Queued ({result.channel})
              </div>
              <div style={{ fontSize: 13, color: "#334155" }}>
                Queued: <strong style={{ color: "#7c3aed" }}>{result.total}</strong> recipient(s)
                {result.absent != null ? <> &nbsp;·&nbsp; Absent in section: <strong style={{ color: "#f59e0b" }}>{result.absent}</strong></> : null}
                {this.state.cancelled > 0 ? <> &nbsp;·&nbsp; <strong style={{ color: "#dc2626" }}>{this.state.cancelled}</strong> cancelled</> : null}
              </div>
              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
                Delivery is processing — see the <strong>Campaign Analysis</strong> tab for Sent / Delivered / Read.
              </div>
              {result.errors && result.errors.length > 0 ? (
                <div style={{ marginTop: 8, maxHeight: 120, overflowY: "auto", fontSize: 11.5, color: "#b91c1c" }}>
                  {result.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              ) : null}
            </div>
            )
          ) : (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px" }}>Recipients</label>
              <div style={{ display: "flex", gap: 8, margin: "8px 0 16px" }}>
                {[
                  { key: "all", label: "All" },
                  { key: "attended", label: "Present" },
                  { key: "absent", label: "Absent" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={sending}
                    onClick={() => this.setState({ recipients: opt.key })}
                    style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                      border: `1.5px solid ${recipients === opt.key ? "#7c3aed" : "#e5e7eb"}`,
                      background: recipients === opt.key ? "#7c3aed" : "#fff",
                      color: recipients === opt.key ? "#fff" : "#475569",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px" }}>Channel</label>
              <div style={{ display: "flex", flexDirection: "row", gap: 8, marginTop: 8 }}>
                {CHANNELS.map((c) => (
                  <div
                    key={c.key}
                    title={c.desc}
                    onClick={() => !sending && this.setState({ channel: c.key })}
                    style={{
                      flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                      border: `1.5px solid ${channel === c.key ? "#7c3aed" : "#e5e7eb"}`,
                      background: channel === c.key ? "#f5f3ff" : "#fff",
                    }}
                  >
                    <span style={{ opacity: channel === c.key ? 1 : 0.55, lineHeight: 0 }}>
                      {c.key === "whatsapp" ? <WhatsAppIcon size={22} /> : <SmsIcon size={22} />}
                    </span>
                    <div style={{ fontWeight: 600, fontSize: 12.5, color: channel === c.key ? "#6d28d9" : "#1f2937" }}>{c.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => this.setState({ showTemplate: !this.state.showTemplate })}
                  style={{ background: "none", border: "none", padding: 0, color: "#7c3aed", fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.6px" }}
                >
                  <i className={`fas fa-chevron-${this.state.showTemplate ? "up" : "down"} mr-1`} />
                  {this.state.showTemplate ? "Hide" : "View"} template{eventCodesForScope.length > 1 ? "s" : ""}
                </button>
                {this.state.showTemplate && (
                  this.state.templatesLoading ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}><span className="spinner-border spinner-border-sm mr-2" />Loading template…</div>
                  ) : (
                    eventCodesForScope.map((code) => {
                      const t = templates[code];
                      // One template covers both scopes; absentees differ only in their
                      // values ("ab/<max>" per subject), not in which template is used.
                      const label = code === "bipc_result" ? "Result — BiPC (Present & Absent)"
                        : code === "mpc_result" ? "Result — MPC (Present & Absent)"
                          : "Result";
                      return (
                        <div key={code} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginTop: 8, background: "#fafafa" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", marginBottom: 4 }}>{label} · <span style={{ color: "#94a3b8" }}>{code}</span></div>
                          {t ? (
                            <>
                              <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{t.template_name} {t.language ? <span style={{ color: "#94a3b8", fontWeight: 400 }}>({t.language})</span> : null}</div>
                              {t.body ? <div style={{ fontSize: 12, color: "#334155", whiteSpace: "pre-wrap", marginTop: 4 }}>{t.body}</div> : null}
                            </>
                          ) : (
                            <div style={{ fontSize: 12, color: "#dc2626" }}>No template mapped for <strong>{code}</strong>.</div>
                          )}
                        </div>
                      );
                    })
                    )
                  )}
                </div>

              {error ? <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: 12.5 }}>{error}</div> : null}
            </>
          )}

          {/* Tabs: Recipients | Campaign Analysis */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #eef2f7" }}>
              {[
                { key: "recipients", label: `Recipients (${recipientList.length})` },
                { key: "analysis", label: "Campaign Analysis" },
              ].map((t) => {
                const active = this.state.activeTab === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => this.setState({ activeTab: t.key })}
                    style={{ background: "none", border: "none", padding: "8px 14px", marginBottom: -2, cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: active ? "#7c3aed" : "#94a3b8", borderBottom: `2px solid ${active ? "#7c3aed" : "transparent"}` }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {this.state.activeTab === "recipients" ? (
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                {recipientList.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>No students in this group.</div>
                ) : recipientList.map((s, i) => {
                  const present = s.attempted_exam == true;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: present ? "#16a34a" : "#f59e0b", flexShrink: 0 }} />
                      <span style={{ color: "#1f2937", fontWeight: 600 }}>{s.student_name || s.mobile}</span>
                      {/* The notification is sent to the PARENT — show father_phone (the real
                          send target), falling back to the student's own mobile. */}
                      <span style={{ marginLeft: "auto", textAlign: "right", lineHeight: 1.35 }}>
                        <span style={{ color: "#334155", fontWeight: 600, display: "block" }}>
                          <i className="fal fa-user-friends" style={{ marginRight: 4, color: "#94a3b8" }} />{s.father_phone || s.mobile || "-"}
                        </span>
                        {s.father_phone && s.mobile && s.father_phone !== s.mobile ? (
                          <span style={{ color: "#b4bcc8", fontSize: 11 }}>student {s.mobile}</span>
                        ) : null}
                      </span>
                      <span style={{ color: present ? "#16a34a" : "#f59e0b", fontWeight: 600, minWidth: 52, textAlign: "right" }}>{present ? "Present" : "Absent"}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                {this.state.campaignsLoading ? (
                  <div style={{ padding: 14, fontSize: 12, color: "#94a3b8", textAlign: "center" }}><span className="spinner-border spinner-border-sm mr-2" />Loading…</div>
                ) : (() => {
                  // Analysis is per channel: SMS and WhatsApp campaigns are shown separately
                  // (WhatsApp has Delivered/Read receipts; SMS only Scheduled/Sent/Failed).
                  const isSms = this.state.channel === "sms";
                  const chCampaigns = (this.state.campaigns || []).filter((c) => (c.channel || "whatsapp") === this.state.channel);
                  if (chCampaigns.length === 0) {
                    return <div style={{ padding: 14, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>No {isSms ? "SMS" : "WhatsApp"} campaigns yet.</div>;
                  }
                  const totalFailed = chCampaigns.reduce((n, c) => n + (c.failed || 0), 0);
                  const totalScheduled = chCampaigns.reduce((n, c) => n + (c.scheduled || 0), 0);
                  return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#fff", borderBottom: "1px solid #f3f4f6" }}>
                      <span style={{ fontSize: 12, color: totalFailed ? "#dc2626" : "#94a3b8", fontWeight: 600 }}>
                        <i className="fas fa-exclamation-triangle mr-1" />{totalFailed} failed
                      </span>
                      {isSms && totalScheduled ? <span style={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}><i className="fas fa-clock mr-1" />{totalScheduled} scheduled</span> : null}
                      {this.state.resendNote ? <span style={{ fontSize: 11.5, color: "#16a34a", marginLeft: 4 }}>{this.state.resendNote}</span> : null}
                      {/* Cancel is available whenever scheduled SMS are still pending — not only
                          right after sending — so it survives closing and reopening the modal. */}
                      {isSms && totalScheduled > 0 ? (
                        <button type="button" disabled={this.state.cancelling} onClick={this.handleCancelScheduled}
                          style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: this.state.cancelling ? "default" : "pointer",
                            border: "1.5px solid #dc2626", background: "#fff", color: "#dc2626", opacity: this.state.cancelling ? 0.6 : 1 }}>
                          <i className="fas fa-ban mr-1" />{this.state.cancelling ? "Cancelling…" : "Cancel scheduled"}
                        </button>
                      ) : (
                        <button type="button" disabled={!totalFailed || this.state.resending}
                          onClick={this.handleResendFailed}
                          style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: (!totalFailed || this.state.resending) ? "not-allowed" : "pointer",
                            border: "1.5px solid #7c3aed", background: (!totalFailed || this.state.resending) ? "#f5f3ff" : "#7c3aed", color: (!totalFailed || this.state.resending) ? "#a78bfa" : "#fff" }}>
                          {this.state.resending ? <><span className="spinner-border spinner-border-sm mr-1" />Resending…</> : <><i className="fas fa-redo mr-1" />Resend Failed</>}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", padding: "6px 12px", background: "#f8fafc", fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      <span style={{ flex: 1 }}>Campaign</span>
                      <span style={{ width: 62, textAlign: "center" }}>Scheduled</span>
                      <span style={{ width: 44, textAlign: "center" }}>Sent</span>
                      {!isSms ? <span style={{ width: 62, textAlign: "center" }}>Delivered</span> : null}
                      {!isSms ? <span style={{ width: 44, textAlign: "center" }}>Read</span> : null}
                      <span style={{ width: 48, textAlign: "center" }}>Failed</span>
                      {isSms ? <span style={{ width: 62, textAlign: "center" }}>Cancelled</span> : null}
                      {isSms ? <span style={{ width: 84, textAlign: "center" }} /> : null}
                    </div>
                    {chCampaigns.map((c) => {
                      const drill = this.state.drill;
                      const isOpen = drill && drill.id === c.id;
                      // A clickable count cell: shows a pointer + underline when it has a value.
                      const cell = (statusKey, value, width, color) => {
                        const n = value || 0;
                        const active = isOpen && drill.statusKey === statusKey;
                        return (
                          <span
                            onClick={n ? () => this.toggleRecipients(c, statusKey) : undefined}
                            title={n ? "Click to see these parents" : ""}
                            style={{ width, textAlign: "center", color: n ? color : "#94a3b8", fontWeight: 600,
                              cursor: n ? "pointer" : "default", textDecoration: n ? "underline dotted" : "none",
                              textUnderlineOffset: 3, background: active ? "#eef2ff" : "transparent", borderRadius: 4 }}>
                            {n}
                          </span>
                        );
                      };
                      const filtered = isOpen
                        ? (this.state.recipientsCache[c.id] || []).filter((r) => this.statusesFor(drill.statusKey).includes(r.status))
                        : [];
                      return (
                        <React.Fragment key={c.id}>
                          <div style={{ display: "flex", alignItems: "center", padding: "7px 12px", borderTop: "1px solid #f3f4f6", fontSize: 12 }}>
                            <span style={{ flex: 1, color: "#1f2937", fontWeight: 600 }}>#{c.id} · {c.campaign_name}</span>
                            {cell("scheduled", c.scheduled, 62, "#b45309")}
                            {cell("sent", c.sent, 44, "#16a34a")}
                            {!isSms ? cell("delivered", c.delivered, 62, "#0891b2") : null}
                            {!isSms ? cell("read", c.read, 44, "#7c3aed") : null}
                            {cell("failed", c.failed, 48, "#dc2626")}
                            {isSms ? cell("cancelled", c.cancelled, 62, "#64748b") : null}
                            {isSms ? (
                              <span style={{ width: 84, textAlign: "center" }}>
                                {c.scheduled > 0 ? (
                                  <button type="button" disabled={this.state.cancellingId === c.id}
                                    onClick={() => this.handleCancelBatch(c)}
                                    title="Cancel this scheduled batch"
                                    style={{ border: "1px solid #dc2626", background: "#fff", color: "#dc2626", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700, cursor: this.state.cancellingId === c.id ? "default" : "pointer", opacity: this.state.cancellingId === c.id ? 0.6 : 1 }}>
                                    {this.state.cancellingId === c.id ? "…" : <><i className="fas fa-ban mr-1" />Cancel</>}
                                  </button>
                                ) : null}
                              </span>
                            ) : null}
                          </div>
                          {isOpen ? (
                            <div style={{ padding: "6px 12px 10px", background: "#f8fafc", borderTop: "1px solid #eef2ff" }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
                                {drill.statusKey} · {filtered.length} parent(s)
                              </div>
                              {this.state.recipientsLoading && !this.state.recipientsCache[c.id] ? (
                                <div style={{ fontSize: 12, color: "#94a3b8" }}><span className="spinner-border spinner-border-sm mr-2" />Loading…</div>
                              ) : filtered.length === 0 ? (
                                <div style={{ fontSize: 12, color: "#94a3b8" }}>No parents in this status.</div>
                              ) : (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {filtered.map((r, i) => (
                                    <span key={i} title={r.error || ""} style={{ fontSize: 12, padding: "3px 9px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff", color: "#1f2937" }}>
                                      <i className="fal fa-user-friends mr-1" style={{ color: "#94a3b8" }} />{r.mobile}{r.name ? ` · ${r.name}` : ""}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </>
                  );
                })()}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          {result ? (
            <Button variant="success" onClick={this.handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="light" onClick={this.handleClose} disabled={sending}>Cancel</Button>
              <Button style={{ background: "#7c3aed", borderColor: "#7c3aed" }} onClick={this.handleSend} disabled={sending}>
                {sending ? <><span className="spinner-border spinner-border-sm mr-2" />Sending…</> : <><i className="fas fa-paper-plane mr-1" /> Send</>}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApollo(NotifyExamStudentsModal);
